import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { SettingsService } from '../../settings.service';
import { User } from '../../../authentication/auth.models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { getDefaultProfilePicUrl, handleImageError } from '../../../utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-settings',
  standalone: false,
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit, OnDestroy {
  @ViewChild('resetPasswordModalContent') resetPasswordModalContent!: TemplateRef<any>;

  protected adminForm!: FormGroup;
  protected profileImageUrl: string | null = null;
  protected imagePreview: string | null = null;
  protected adminDetails: User | null = null;
  protected isAdminDetailsLoading: boolean = true;
  protected isAdminDetailsSaving: boolean = false;
  protected selectedProfileImageFile: File | null = null;
  protected activeTab: string = 'profile';

  private _modalRef!: NgbModalRef;
  private _settingsSubscription!: Subscription;

  constructor(
    private _settingsService: SettingsService,
    private _modalService: NgbModal,
    private _permissionService: PermissionService
  ) {}

  /** The permission tree gates the tab, not is_staff: hasPermission already lets staff through. */
  protected get canManagePermissions(): boolean {
    return this._permissionService.hasPermission('ui_settings.permissions.view');
  }

  ngOnInit(): void {
    this._getAdminDetails();
  }

  // Protected methods

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.selectedProfileImageFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  protected onSubmit(): void {
    if (this.adminForm.invalid) return;

    this.isAdminDetailsSaving = true;

    const formData = new FormData();

    // Add dirty fields only
    Object.keys(this.adminForm.controls).forEach(key => {
      const control = this.adminForm.get(key);
      if (control && control.dirty) {
        formData.append(key, control.value);
      }
    });

    // Append profile picture file only if selected
    if (this.selectedProfileImageFile) {
      formData.append('profile_picture', this.selectedProfileImageFile);
    } else {
      formData.append('profile_picture', '');
    }

    this._settingsSubscription = this._settingsService
      .patch(formData)
      .pipe(finalize(() => (this.isAdminDetailsSaving = false)))
      .subscribe({
        next: (updatedAdmin: User) => {
          // Update the profile image URL with the new saved image
          this.profileImageUrl = updatedAdmin?.profile_picture;
          // Clear the preview and selected file after successful save
          this.imagePreview = null;
          this.selectedProfileImageFile = null;
          // Update admin details
          this.adminDetails = updatedAdmin;
        },
      });
  }

  protected removeProfilePicture(): void {
    this.imagePreview = null;
    this.profileImageUrl = null;
    this.selectedProfileImageFile = null;
  }

  protected openResetPasswordModal(): void {
    this._modalRef = this._modalService.open(this.resetPasswordModalContent, { size: 'lg', backdrop: 'static' });
  }

  // Private methods

  private _getAdminDetails(): void {
    this.isAdminDetailsLoading = true;
    this._settingsSubscription = this._settingsService
      .get()
      .pipe(
        finalize(() => {
          this.isAdminDetailsLoading = false;
        })
      )
      .subscribe({
        next: (adminDetails: User) => {
          this.adminDetails = adminDetails;
          this._initializeAdminForm();
        },
      });
  }

  private _initializeAdminForm(): void {
    this.adminForm = new FormGroup({
      email: new FormControl(this.adminDetails?.email, [Validators.required, Validators.email]),
      first_name: new FormControl(this.adminDetails?.first_name, Validators.required),
      last_name: new FormControl(this.adminDetails?.last_name, Validators.required),
      phone_number: new FormControl(this.adminDetails?.phone_number, [Validators.pattern(/^[0-9]{10}$/)]),
      address_line1: new FormControl(this.adminDetails?.address_line1),
      address_line2: new FormControl(this.adminDetails?.address_line2),
      postal_code: new FormControl(this.adminDetails?.postal_code, [Validators.pattern(/^[0-9]{6}$/)]),
      city: new FormControl(this.adminDetails?.city),
      country: new FormControl(this.adminDetails?.country),
    });

    this.profileImageUrl = this.adminDetails?.profile_picture ?? null;
  }

  ngOnDestroy(): void {
    this._settingsSubscription?.unsubscribe();
  }

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;
  protected readonly handleImageError = handleImageError;
}
